<script>
  /**
   * Compact media-activity indicator for the DynamicIsland: the one place that
   * answers "is anything still happening with my uploads?".
   * Uploading is determinate and loud; optimizing is informational and calm;
   * failures collect here with per-item retry.
   */
  import Popover from '#lib/Popover.svelte';
  import ButtonLabel from '#lib/ButtonLabel.svelte';
  import Icon from '#lib/Icon.svelte';
  import t from '#lib/i18n.svelte.js';
  import { prettify_file_name } from '#lib/util.js';
  import { get_jobs, retry, remove } from '#lib/upload_manager.svelte.js';

  let anchor_ref = $state();
  let popover_ref = $state();

  let jobs = $derived(get_jobs());
  let uploading = $derived(jobs.filter(j => ['queued', 'uploading', 'creating'].includes(j.status)));
  let optimizing = $derived(jobs.filter(j => j.status === 'optimizing'));
  let failed = $derived(jobs.filter(j => j.status === 'failed'));

  /* Failed beats uploading beats optimizing: one clear label, one count —
     the "Löschen (3)" pattern, not a strip of cryptic glyph counters. */
  let chip = $derived.by(() => {
    if (failed.length > 0) return { icon: 'warning', label: `${t('upload_failed')} (${failed.length})`, failed: true };
    if (uploading.length > 0) return { icon: 'share', label: `${t('uploading')} (${uploading.length})` };
    return { icon: 'cycle', label: `${t('optimizing')} (${optimizing.length})` };
  });

  /* Entries get renamed in the uploader table — show the entry title, never
     the raw filename with extension. */
  function job_title(job) {
    return job.entry?.title || prettify_file_name(job.file_name.split('.')[0]);
  }
</script>

<!-- Only while something is actually happening — lingering 'ready' jobs
     (kept briefly for placement UIs) must not show a zero-count chip -->
{#if uploading.length + optimizing.length + failed.length > 0}
  <button
    bind:this={anchor_ref}
    class='media-activity-chip'
    popovertarget='media_activity_popover'
    title={t('add_media')}
  >
    <ButtonLabel variant='brand ghostly text-compact' start_icon={chip.icon}>
      <span class='activity-label' class:failed-text={chip.failed}>{chip.label}</span>
    </ButtonLabel>
  </button>

  <Popover
    anchor={anchor_ref}
    bind:ref={popover_ref}
    id='media_activity_popover'
    placement='bottom-end'
    class='normal'
    offset={6}
  >
    <div class='media-activity flex-column py p-3'>
      <ul class='media-activity-list flex-column'>
        {#each jobs as job (job.id)}
          <li class='flex-row items-center g-3 gap-x px p-4 py p-2'>
            {#if job.preview_url}
              <img class='job-thumb' src={job.preview_url} alt='' />
            {:else}
              <span class='job-thumb job-thumb-empty'><Icon name='image' /></span>
            {/if}
            <span class='job-title text-control truncate'>{job_title(job)}</span>
            {#if job.status === 'uploading'}
              <progress max='100' value={job.progress}></progress>
            {:else if job.status === 'failed'}
              <button onclick={() => retry(job.id)}>
                <ButtonLabel variant='outlined text-compact'>{t('retry')}</ButtonLabel>
              </button>
              <button onclick={() => remove(job.id)}>
                <ButtonLabel variant='ghostly text-compact'>{t('remove')}</ButtonLabel>
              </button>
            {:else if job.status === 'optimizing'}
              <span class='loading-spinner' title={t('optimizing')}></span>
            {:else if job.status === 'ready'}
              <Icon name='check' />
            {/if}
          </li>
        {/each}
      </ul>
      {#if optimizing.length > 0}
        <!-- The reassurance sentence appears ONCE, not per row -->
        <p class='text-caption text-3 px p-4 py p-2 optimizing-note'>{t('optimizing_safe_to_leave')}</p>
      {/if}
    </div>
  </Popover>
{/if}

<style>
  .media-activity-chip .activity-label {
    white-space: nowrap;
    font-variant-numeric: tabular-nums; /* counts tick without layout shift */
  }
  .failed-text {
    color: var(--color-system-destructive, #b3382f);
  }
  .media-activity {
    max-width: 320px;
  }
  .media-activity-list {
    margin: 0;
    padding-inline-start: 0;
    list-style: none;
    font-variant-numeric: tabular-nums;
  }
  .job-title {
    flex: 1;
    min-width: 0;
  }
  .job-thumb {
    width: 36px;
    height: 36px;
    flex-shrink: 0;
    object-fit: cover;
    border-radius: var(--radius-sm);
    border: none; /* the global img rule would paint a default border */
    background: var(--color-default-background-100, var(--color-default-background));
  }
  .job-thumb-empty {
    display: flex;
    align-items: center;
    justify-content: center;
    --icon-size: 16px;
  }
  progress {
    width: 72px;
    flex-shrink: 0;
    accent-color: var(--editing);
  }
  /* Job spinner: the shared .loading-spinner from animations.css */
  .optimizing-note {
    max-width: 100%;
  }
</style>
